import type { DropdownValue, FlatItem, MatchesItem, NestedGroupItem } from '../../types';

function matchesSearch(item: NestedGroupItem, normalizedSearch: string): boolean {
  return (
    item.label.toLowerCase().includes(normalizedSearch) ||
    item.value.toLowerCase().includes(normalizedSearch)
  );
}

export function getSearchExpandedValues(
  items: NestedGroupItem[],
  searchTerm: string,
  matchesItem: MatchesItem = (item, term) => matchesSearch(item, term.toLowerCase()),
): DropdownValue[] {
  const normalizedSearch = searchTerm.trim();
  const expandedValues: DropdownValue[] = [];

  if (!normalizedSearch) {
    return expandedValues;
  }

  const visit = (item: NestedGroupItem): boolean => {
    let hasMatchingDescendant = false;

    for (const child of item.children) {
      if (visit(child)) {
        hasMatchingDescendant = true;
      }
    }

    if (hasMatchingDescendant) {
      expandedValues.push(item.value);
    }

    return hasMatchingDescendant || matchesItem(item, normalizedSearch);
  };

  items.forEach(visit);

  return expandedValues;
}

export function filterNestedGroups(
  items: NestedGroupItem[],
  searchTerm: string,
  hideSearchParents = false,
  matchesItem: MatchesItem = (item, term) => matchesSearch(item, term.toLowerCase()),
): NestedGroupItem[] {
  const normalizedSearch = searchTerm.trim();

  if (!normalizedSearch) {
    return items;
  }

  return items.flatMap((item) => {
    const itemMatches = matchesItem(item, normalizedSearch);

    if (item.children.length === 0) {
      return itemMatches ? [item] : [];
    }

    if (itemMatches) {
      return [item];
    }

    const filteredChildren = filterNestedGroups(
      item.children,
      normalizedSearch,
      hideSearchParents,
      matchesItem,
    );

    if (filteredChildren.length === 0) {
      return [];
    }

    return hideSearchParents ? filteredChildren : [{ ...item, children: filteredChildren }];
  });
}

export function flattenItemsForRender(items: NestedGroupItem[], depth = 0): FlatItem[] {
  return items.flatMap((item) => [
    {
      label: item.label,
      value: item.value,
      type: item.type,
      selectedLabel: item.selectedLabel,
      customValue: item.customValue,
      description: item.meta?.description,
      depth,
    },
    ...flattenItemsForRender(item.children, depth + 1),
  ]);
}
